import { HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { BehaviorSubject, Observable, Subject, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { isPlatformBrowser } from '@angular/common';
import { environment } from '../../../environments/environment';
import { CommonHttpClientService } from './common-http-client.service';
import { GlobalComponentService } from '@techmore/global';
import { parseAuthoriztionToken } from 'src/app/util';
import { promise } from 'protractor';
import { GENERATOR_ENV_TOKEN, GeneratorEnv } from '../../web-generator/generator-env-token';


/** 管理授權權杖服務 */
@Injectable({
  providedIn: 'root'
})
export class TokenService {
  /** 是否正在刷新token中 */
  private _isRefreshingToken = false;
  /** 是否正在刷新token中 */
  public get isRefreshingToken() { return this._isRefreshingToken; }
  private _tokenRefreshed$ = new Subject<void>();
  /** 登出剩餘時間 */
  private _logoutTimer$ = new BehaviorSubject<number>(null);
  /** 是否正在登出中 */
  private _isLoggingOut = false;
  /** 是否正在登出中 */
  public get isLoggingOut() { return this._isLoggingOut; }
  /** 登入後要自動轉導回的頁面 */
  private _redirectUrl: string;
  get userInfo() {
    const jwtPayload = this.getJWTpayload();
    return {
      userName: jwtPayload && jwtPayload.sub,
      ...jwtPayload
    };
  }
  readonly auth_token_key: string = 'auth_token';
  readonly auth_refresh_token_key: string = 'auth_refresh_token';
  readonly jwt_payload_key: string = 'memberInfo';
  readonly auth_way_key: string = 'auth_way';

  constructor(
    private http: CommonHttpClientService,
    private router: Router,
    private globalComponentService: GlobalComponentService,
    @Inject(PLATFORM_ID) private platformId: Object,
    @Inject(GENERATOR_ENV_TOKEN) private generatorEnv: GeneratorEnv,
  ) { }

  /** 進入應授權落腳頁就馬上調用
   * @throws 若授權不存在就拋出Error
   */
  public init() {
    if (isPlatformBrowser(this.platformId)) {
      this.setLogoutTimer();
    }
  }

  /** 取得登出剩餘時間，每次更新權杖/進入頁面初始化後才會推播一次剩餘秒數 */
  public getLogoutTimer$(): Observable<number> {
    return this._logoutTimer$.asObservable();
  }

  /** 更新存取根
   * @throws 若授權不存在就拋出Error
   */
  public refreshToken$(apiObs?: Observable<any>): Observable<any> {
    if (this._isRefreshingToken) {
      return new Observable(observer => {
        const subc = this._tokenRefreshed$.subscribe(() => {
          subc.unsubscribe();
          observer.next();
          observer.complete();
        }, error => {
          subc.unsubscribe();
          observer.error(error);
          observer.complete();
        });
      });
    }
    this._isRefreshingToken = true;

    return (apiObs ? apiObs : this.http.post(`${this.generatorEnv.apiHost}/system/oauth/token/refresh`, {},
      { headers: { Authorization: 'Bearer ' + localStorage.getItem(this.auth_refresh_token_key) } }
    )).pipe(
      tap(((jwtToken) => {
        this._isRefreshingToken = false;
        const refreshToken = jwtToken['refreshToken'];
        const accessToken = jwtToken['accessToken'];
        localStorage.setItem(this.auth_token_key, accessToken);
        localStorage.setItem(this.auth_refresh_token_key, refreshToken);
        // localStorage.setItem(this.jwt_payload_key, parseAuthoriztionToken(accessToken));
        this.setLogoutTimer();
        this._tokenRefreshed$.next();
      })),
      catchError((err: HttpErrorResponse) => {
        console.error('refreshing Token error');
        // this.globalComponentService.openErrorAlert('頁面閒置太久，請重新登入');
        this._isRefreshingToken = false;
        this._tokenRefreshed$.error(err);
        return throwError(() => err);
      })
    );
  }

  /** 是否可以登出 */
  public canLogout(): boolean {
    return !this._isLoggingOut && !!localStorage.getItem(this.auth_token_key);
  }

  /** 系統登出
   * @returns 返回承諾導航結果，若操作前已登出或正在登出中則 resolve false
   */
  public logout(): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      if (!this.canLogout()) { resolve(false); return; }
      this._isLoggingOut = true;
      
      this._customLogout().then(_ => {
        localStorage.removeItem(this.auth_refresh_token_key);
        localStorage.removeItem(this.auth_token_key);
        localStorage.removeItem(this.jwt_payload_key);
        localStorage.removeItem(this.auth_way_key);
        this._logoutTimer$.next(0);
        this._isLoggingOut = false;
        resolve(_);
      }).catch(error => {
        localStorage.removeItem(this.auth_refresh_token_key);
        localStorage.removeItem(this.auth_token_key);
        localStorage.removeItem(this.jwt_payload_key);
        localStorage.removeItem(this.auth_way_key);
        this._logoutTimer$.next(0);
        this._isLoggingOut = false;
        reject(error);
      });
    });
  }

  /** 設定距離登出時間剩餘多少秒數
   * @throws 若授權不存在就拋出Error
   */
  protected setLogoutTimer(): void {
    const expDate = this.getExpTime();
    const expirationDuration = Math.floor((expDate.getTime() - Date.now()) / 1000); // 從 token 獲取剩餘秒數
    this._logoutTimer$.next(expirationDuration < 0 ? 0 : expirationDuration);
  }

  /** 取得授權權杖
   * @throws 若授權不存在就拋出Error
   */
  public getAuthToken(): string {
    const authorization = localStorage.getItem(this.auth_token_key);
    if (authorization) {
      return authorization;
    } else {
      throw new Error('Token not existing');
    }
  }

  /** 取得更新授權權杖
   * @throws 若授權不存在就拋出Error
   */
  public getRefreshToken(): string {
    const authorization = localStorage.getItem(this.auth_refresh_token_key);
    if (authorization) {
      return authorization;
    } else {
      throw new Error('Token not existing');
    }
  }

  /** 取得權杖解析後的資料包
   * @throws 若授權不存在就拋出Error
   */
  public getJWTpayload(): any {
    const authorization = localStorage.getItem(this.jwt_payload_key);
    if (authorization) {
      return JSON.parse(authorization);
    } else {
      return JSON.parse(parseAuthoriztionToken(this.getAuthToken()));
    }
  }

  /** 取得TOKEN過期時間
   * @throws 若授權不存在就拋出Error
   */
  public getExpTime(): Date {
    const p = this.getJWTpayload();
    return new Date((p.exp as number) * 1000);
  }

  /** 檢查 更新存取根 到期時間是否未到要求的時間點
   * @returns 超過指定時間回傳真 ，否則為假
   * @throws 若授權不存在就拋出Error
   */
  public checkRefreshTokenExpiredTime(endDate: Date): boolean {
    const refreshToken = this.getRefreshToken();
    const refreshInfo = parseAuthoriztionToken(refreshToken);
    const pareInfo = JSON.parse(refreshInfo);
    const refreshExp = pareInfo.exp;
    if (refreshExp < endDate.getTime() / 1000) {
      return true;
    }
    return false;
  }

  /** 取得登入後要自動轉導回的頁面 */
  public getRedirectUrl(): string {
    return this._redirectUrl;
  }
  
  /** 設定登入後要自動轉導回的頁面 */
  public setRedirectUrl(url: string): void {
    this._redirectUrl = url;
  }

  /** 清除登入後要自動轉導回的頁面 */
  public clearRedirectUrl(): void {
    delete this._redirectUrl;
  }

  /** 登出行為，可透過 registCustomLogout 方法覆寫 */
  protected _customLogout: () => Promise<boolean> = () => {
    return this.router.navigate(['/login']);
  };

  /** 註冊自訂登出行為 */
  public registCustomLogout(func: () => Promise<boolean>): void {
    if (func) {
      this._customLogout = func;
    }
  }
}
